import { parse } from "papaparse";

export type TCsvType = { name: string; fields: string[]; title: string; fileName: string };

const parentChildRelationShip = ["parent_localid", "student_localid"];
const teacherChildRelationShip = ["student_localid", "teacher_localid"];
const teacherTeamRelationShip = ["team_name", "teacher_localid", "room", "virtual_room"];
const TeamStudentRelationShip = ["team_name", "student_localid"];

export const csvTypes = [
  {
    name: "teachers",
    fields: [
      "teacher_localid",
      "firstname",
      "lastname",
      "email",
      "phonenumber",
      "titledescription",
      "virtual_room",
      "room",
      "username",
      "password"
    ],
    requiredFields: [
      "teacher_localid",
      "firstname",
      "lastname",
      "email"
    ],
    title: "Teachers",
    fileName: "teacher"
  },
  {
    name: "parents",
    fields: [
      "parent_localid",
      "firstname",
      "lastname",
      "email",
      "phonenumber",
      "username",
      "password"
    ],
    requiredFields: [
      "parent_localid",
      "firstname",
      "lastname",
      "username",
    ],
    title: "Parents",
    fileName: "parent"
  },
  {
    name: "students",
    fields: [
      "student_localid",
      "firstname",
      "lastname",
      "description",
      "email",
      "phonenumber",
      "username",
      "password"
    ],
    requiredFields: [
      "student_localid",
      "firstname",
      "lastname"
    ],
    title: "Students",
    fileName: "student"
  },
  {
    name: "parent_student",
    fields: parentChildRelationShip,
    requiredFields: parentChildRelationShip,
    title: "Relationships",
    fileName: "relationship"
  },
  {
    name: "teacher_student",
    fields: teacherChildRelationShip,
    requiredFields: teacherChildRelationShip,
    title: "Enrollment",
    fileName: "enrollment"
  },
  {
    name: "teacher_team",
    fields: teacherTeamRelationShip,
    requiredFields: teacherTeamRelationShip,
    title: "Team",
    fileName: "team"
  },
  {
    name: "team_student",
    fields: TeamStudentRelationShip,
    requiredFields: TeamStudentRelationShip,
    title: "Team Enrollment",
    fileName: "team_enrollment"
  }
] as const;
export const validateCsv = (str: string, item: TCsvType, missingOnly = false) => {
  const selected = csvTypes.find((i) => i.name === item.name);
  const validMessages = [] as { type: "error" | "warning"; message: string }[];
  if (selected && str) {
    const data = parse(str.trimEnd()).data as string[][];
    const headers = data[0] || [];

    let valid = true;
    selected.fields.forEach((i) => {   
      const occurrence = headers.filter((h) => h == i).length;
      if (occurrence == 1) return;
      if (occurrence > 1){
        validMessages.push({ type: "error", message: `Found duplicate header "${i}" in csv file` });
        valid = false;
      }
    });
    selected.requiredFields.forEach((i) => {
      const occurrence = headers.filter((h) => h == i).length;
      if (occurrence == 1) return;
      if (occurrence < 1)
        validMessages.push({ type: "error", message: `Missing field "${i}"` });
      valid = false;
    });
    if (!missingOnly) {
      headers.forEach((header, i) => {
        if (selected.fields.includes(header as any)) return;
        if (i === headers.length - 1 && !header)
          validMessages.push({ type: "error", message: `Unsupported trailing comma` });
        else if (headers.indexOf(header) === i)
          validMessages.push({
            type: "warning",
            message: `Unsupported header "${header}" in csv file`
          });
        valid = false;
      });
      // order validation
      // const OOOrder = (selected.fields as string[]).reduce((pre, curr, i) => {
      //   if (headers[i] !== curr) pre.push(curr);
      //   return pre;
      // }, [] as string[]);
      // if (OOOrder.length) {
      //   valid = false;
      //   validMessages.push({
      //     message: `The following fields are out of order:
      //     ${OOOrder.join(", ")}.
      //     The correct order is:
      //     ${selected.fields.join(", ")}`,
      //     type: "error"
      //   });
      // }
    }
    if (!data.every((i) => i.length >= selected.requiredFields.length)) {
      valid = false;
      validMessages.push({
        message: `Each row must have ${selected.requiredFields.length} fields`,
        type: "error"
      });
    }

    return { valid, validMessages };
  }
  return { valid: false, validMessages };
};

export const synergyParentVueSsoUrl = (
  districtId: number | string,
  schoolId: number | string
) =>
  `https://api.ptcwizard.com/api/sso/synergy?account=${districtId}&school=${schoolId}&role=parent&SAT=`;

export type SynergySsoConfig = {
  pxp_url: string;
  application_name: string;
};

export const parseSynergySsoConfig = (data: unknown): SynergySsoConfig => {
  const row = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const nested =
    row.synergy_sso && typeof row.synergy_sso === "object"
      ? (row.synergy_sso as Record<string, unknown>)
      : {};
  return {
    pxp_url: String(nested.pxp_url ?? row.pxp_url ?? ""),
    application_name: String(nested.application_name ?? row.application_name ?? ""),
  };
};

export const integrationTypes = [
  { value: "none", title: "None" },
  { value: "csv", title: "CSV" },
  { value: "hybrid", title: "CSV with Parent Registration" },
  { value: "blackbaud", title: "BlackBaud Sky API" },
  // Kept for existing schools; not offered in the type dropdown.
  { value: "blackbaud_new", title: "BlackBaud SKY API (new)" },
  { value: "nycdoe", title: "NYC DOE" },
  { value: "infinitecampus", title: "Infinite Campus" },
  { value: "synergy", title: "Synergy" },
  { value: "oneroster", title: "OneRoster API"},
  { value: "facts", title: "Facts API" },
  { value: "sungard", title: "Sungard eSchoolPlus" },
  { value: "google", title: "Google Sign-On for Teachers" },
  { value: "finalsite", title: "FinalSite" },
  { value: "seniorsystems", title: "Senior Systems/MyBackpack" },
] as const;

export const selectableIntegrationTypes = integrationTypes.filter(
  (type) => type.value !== "blackbaud_new"
);
